import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import { Save, Cancel } from "../themes/IconButtonSet";
import CustomDatePicker from "./CustomDatePicker";

const VaccineUsageEvaluateQuestionnaire = () => {
  const navigate = useNavigate();
  const { sessionId } = useAuth();
  const [formData, setFormData] = useState({
    EvaluateDate: new Date().toISOString().split('T')[0], // Default to today's date
    EvaluateName: "",
    EvaluateCompany: "",
    Province: "",
    VaccineID: "",
    LotID: "",
    ReceiveDate: new Date().toISOString().split('T')[0], // Default to today's date
    UsedDate: new Date().toISOString().split('T')[0], // Default to today's date
    LivestockTypeID: "",
    LivestockAmount: "",
    AllergicAmount: "",
    AllergicDetail: "",
    SideEffectAmount: "",
    SideEffectDetail: "",
    SatisfactionSystemEaseOfUse: "", // New field for 4.1
    SatisfactionSystemResponsiveness: "", // New field for 4.2
    Remark: "", // General remarks
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [vaccineTypes, setVaccineTypes] = useState([]);
  const [livestockTypes, setLivestockTypes] = useState([]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login");
    } else {
      fetchVaccineTypes();
      fetchLivestockTypes();
    }
  }, [sessionId, navigate]);

  // Fetch Vaccine Types
  const fetchVaccineTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineTypes(response.VaccinesList || []);
      }
    } catch (error) {
      console.error("Error fetching vaccine types:", error);
    }
  };

  // Fetch Livestock Types
  const fetchLivestockTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLivestockTypes(response.LivestockTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleRadioChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "EvaluateName":
        if (!value) newErrors[name] = "กรุณากรอกชื่อผู้ประเมิน";
        else delete newErrors[name];
        break;
      case "EvaluateCompany":
        if (!value) newErrors[name] = "กรุณากรอกชื่อหน่วยงาน/ฟาร์ม";
        else delete newErrors[name];
        break;
      case "Province":
        if (!value) newErrors[name] = "กรุณากรอกจังหวัด";
        else delete newErrors[name];
        break;
      case "VaccineID":
        if (!value) newErrors[name] = "กรุณาเลือกชื่อวัคซีน";
        else delete newErrors[name];
        break;
      case "LotID":
        if (!value) newErrors[name] = "กรุณากรอกรหัสล็อตวัคซีน";
        else delete newErrors[name];
        break;
      case "LivestockTypeID":
        if (!value) newErrors[name] = "กรุณาเลือกชนิดสัตว์ที่ได้รับวัคซีน";
        else delete newErrors[name];
        break;
      case "LivestockAmount":
        if (!value || isNaN(value) || parseInt(value) <= 0) newErrors[name] = "กรุณาระบุจำนวนสัตว์ที่ได้รับวัคซีนที่ถูกต้อง";
        else delete newErrors[name];
        break;
      case "AllergicAmount":
        if (value && (isNaN(value) || parseInt(value) < 0)) newErrors[name] = "กรุณาระบุจำนวนสัตว์ที่แพ้วัคซีนที่ถูกต้อง";
        else delete newErrors[name];
        break;
      case "SideEffectAmount":
        if (value && (isNaN(value) || parseInt(value) < 0)) newErrors[name] = "กรุณาระบุจำนวนสัตว์ที่มีอาการข้างเคียงที่ถูกต้อง";
        else delete newErrors[name];
        break;
      case "SatisfactionSystemEaseOfUse":
      case "SatisfactionSystemResponsiveness":
        if (!value) newErrors[name] = "กรุณาให้คะแนนความพึงพอใจ";
        else delete newErrors[name];
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    const fieldsToValidate = [
      "EvaluateName", "EvaluateCompany", "Province", "VaccineID", "LotID",
      "LivestockTypeID", "LivestockAmount", "SatisfactionSystemEaseOfUse",
      "SatisfactionSystemResponsiveness"
    ];

    let currentErrors = {};
    fieldsToValidate.forEach(field => {
      validateField(field, formData[field]);
      if (errors[field]) {
        currentErrors[field] = errors[field];
      }
    });

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      // Use a custom message box instead of alert
      console.log("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนและถูกต้อง");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/submitVaccineUsageEvaluate", // New API endpoint
        data: {
          ...formData,
          CreateBy: formData.EvaluateName, // Assuming CreateBy is EvaluateName
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        // Use a custom message box instead of alert
        alert("ระบบได้บันทึกแบบประเมินผลการใช้วัคซีนของคุณแล้ว ขอขอบคุณที่ร่วมทำแบบแบบประเมินผลการใช้วัคซีน เราจะนำข้อมูลไปปรับปรุงการให้บริการ");
        handleCancel();
        navigate("/home"); // Or wherever you want to navigate after submission
      } else {
        // Use a custom message box instead of alert
        console.log("เกิดข้อผิดพลาด: " + response.error);
      }
    } catch (error) {
      console.error("Error submitting evaluation:", error);
      // Use a custom message box instead of alert
      console.log("เกิดข้อผิดพลาดในการส่งแบบประเมิน \r\r" + error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      EvaluateDate: new Date().toISOString().split('T')[0],
      EvaluateName: "",
      EvaluateCompany: "",
      Province: "",
      VaccineID: "",
      LotID: "",
      ReceiveDate: new Date().toISOString().split('T')[0],
      UsedDate: new Date().toISOString().split('T')[0],
      LivestockTypeID: "",
      LivestockAmount: "",
      AllergicAmount: "",
      AllergicDetail: "",
      SideEffectAmount: "",
      SideEffectDetail: "",
      SatisfactionSystemEaseOfUse: "",
      SatisfactionSystemResponsiveness: "",
      Remark: "",
    });
    setErrors({});
  };

  if (!sessionId) {
    return null;
  }

  const satisfactionOptions = [
    { value: "5", label: "5 (มากที่สุด)" },
    { value: "4", label: "4 (มาก)" },
    { value: "3", label: "3 (ปานกลาง)" },
    { value: "2", label: "2 (น้อย)" },
    { value: "1", label: "1 (น้อยที่สุด)" },
  ];

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              แบบประเมินผลการใช้วัคซีน
            </Heading>
          </HStack>
        </Box>

        <Box
          p={5}
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          w="100%"
          bg="white"
        >
          {/* Section 1: ข้อมูลทั่วไป */}
          <Heading size="md" mb={4}>
            ตอนที่ 1: ข้อมูลทั่วไป
          </Heading>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl>
                <FormLabel>วันที่ประเมิน</FormLabel>
                <CustomDatePicker
                  name="EvaluateDate"
                  value={formData.EvaluateDate}
                  onChange={handleInputChange}
                  maxDate={new Date()} 
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.EvaluateCompany}>
                <FormLabel>ชื่อหน่วยงาน/ฟาร์ม</FormLabel>
                <Input
                  name="EvaluateCompany"
                  value={formData.EvaluateCompany}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.EvaluateCompany}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.EvaluateName}>
                <FormLabel>ชื่อผู้ประเมิน</FormLabel>
                <Input
                  name="EvaluateName"
                  value={formData.EvaluateName}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.EvaluateName}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.Province}>
                <FormLabel>จังหวัด</FormLabel>
                <Input
                  name="Province"
                  value={formData.Province}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.Province}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>

          {/* Section 2: รายละเอียดวัคซีนที่ใช้ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 2: รายละเอียดวัคซีนที่ใช้
          </Heading>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.VaccineID}>
                <FormLabel>ชื่อวัคซีน</FormLabel>
                <Select
                  name="VaccineID"
                  value={formData.VaccineID}
                  onChange={handleInputChange}
                  placeholder="เลือกวัคซีน"
                  colorScheme="iBlue"
                >
                  {vaccineTypes.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.VaccineID}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.LotID}>
                <FormLabel>รหัสล็อตวัคซีน</FormLabel>
                <Input
                  name="LotID"
                  value={formData.LotID}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.LotID}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl>
                <FormLabel>วันที่ได้รับวัคซีน</FormLabel>
                <CustomDatePicker
                  name="ReceiveDate"
                  value={formData.ReceiveDate}
                  onChange={handleInputChange}
                  maxDate={new Date()} 
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl>
                <FormLabel>วันที่ใช้วัคซีน</FormLabel>
                <CustomDatePicker
                  name="UsedDate"
                  value={formData.UsedDate}
                  onChange={handleInputChange}
                  maxDate={new Date()} 
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.LivestockTypeID}>
                <FormLabel>ชนิดสัตว์ที่ได้รับวัคซีน</FormLabel>
                <Select
                  name="LivestockTypeID"
                  value={formData.LivestockTypeID}
                  onChange={handleInputChange}
                  placeholder="เลือกชนิดสัตว์"
                  colorScheme="iBlue"
                >
                  {livestockTypes.map((type) => (
                    <option
                      key={type.LivestockTypeID}
                      value={type.LivestockTypeID}
                    >
                      {type.LivestockTypeName}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.LivestockTypeID}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.LivestockAmount}>
                <FormLabel>จำนวนสัตว์ที่ได้รับวัคซีน</FormLabel>
                <Input
                  name="LivestockAmount"
                  type="number"
                  value={formData.LivestockAmount}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.LivestockAmount}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>

          {/* Section 3: ผลการใช้งาน */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 3: ผลการใช้งาน
          </Heading>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isInvalid={!!errors.AllergicAmount}>
                <FormLabel>3.1 การแพ้วัคซีน: ระบุจำนวนสัตว์ที่แพ้</FormLabel>
                <Input
                  name="AllergicAmount"
                  type="number"
                  value={formData.AllergicAmount}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.AllergicAmount}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl>
                <FormLabel>รายละเอียดการแพ้</FormLabel>
                <Textarea
                  name="AllergicDetail"
                  value={formData.AllergicDetail}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isInvalid={!!errors.SideEffectAmount}>
                <FormLabel>3.2 อาการข้างเคียงหลังการฉีด: ระบุจำนวนสัตว์ที่มีอาการ</FormLabel>
                <Input
                  name="SideEffectAmount"
                  type="number"
                  value={formData.SideEffectAmount}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.SideEffectAmount}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}></GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl>
                <FormLabel>รายละเอียดอาการข้างเคียง</FormLabel>
                <Textarea
                  name="SideEffectDetail"
                  value={formData.SideEffectDetail}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
          </Grid>

          {/* Section 4: ความพึงพอใจในระบบ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 4: ความพึงพอใจในระบบ (คะแนน 1-5)
          </Heading>
          <VStack align="start" spacing={4}>
            <FormControl isRequired isInvalid={!!errors.SatisfactionSystemEaseOfUse}>
              <FormLabel>4.1 ความสะดวกในการใช้งานระบบ</FormLabel>
              <RadioGroup
                name="SatisfactionSystemEaseOfUse"
                value={formData.SatisfactionSystemEaseOfUse}
                onChange={(value) => handleRadioChange("SatisfactionSystemEaseOfUse", value)}
              >
                <HStack spacing={4}>
                  {satisfactionOptions.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </HStack>
              </RadioGroup>
              <FormErrorMessage>{errors.SatisfactionSystemEaseOfUse}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={!!errors.SatisfactionSystemResponsiveness}>
              <FormLabel>4.2 ตอบสนองความต้องการของท่านในระดับใด</FormLabel>
              <RadioGroup
                name="SatisfactionSystemResponsiveness"
                value={formData.SatisfactionSystemResponsiveness}
                onChange={(value) => handleRadioChange("SatisfactionSystemResponsiveness", value)}
              >
                <HStack spacing={4}>
                  {satisfactionOptions.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </HStack>
              </RadioGroup>
              <FormErrorMessage>{errors.SatisfactionSystemResponsiveness}</FormErrorMessage>
            </FormControl>
          </VStack>

          {/* Section 5: ข้อเสนอแนะอื่นๆ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 5: ข้อเสนอแนะอื่นๆ
          </Heading>
          <FormControl>
            <FormLabel>ข้อเสนอแนะอื่นๆ</FormLabel>
            <Textarea
              name="Remark"
              value={formData.Remark}
              onChange={handleInputChange}
              colorScheme="iBlue"
            />
          </FormControl>

          {/* Buttons */}
          <HStack mt={6} justify="flex-end">
            <Cancel onClick={handleCancel} label="ยกเลิก" />
            <Save onClick={handleSubmit} isLoading={isLoading} label="ส่งแบบประเมิน" />
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default VaccineUsageEvaluateQuestionnaire;
